import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import { config } from '../../config';
import ExpandMore from '../icons/ExpandMore';
import ChevronRight from '../icons/ChevronRight';
import DragIndicator from '../icons/DragIndicator';
import MoreHoriz from '../icons/MoreHoriz';
import Add from '../icons/Add';

const ListViewGroupHeader = ({
  list,
  isCollapsed,
  onToggleCollapse,
  cardCount,
  moveCard,
  moveActionList,
  setBoard,
  isReadOnly,
  boardId,
  workspaceId,
  collapseAllGroups,
  restoreCollapsedGroups,
  onAddCard,
}) => {
  const headerRef = useRef(null);
  const dragHandleRef = useRef(null);
  const titleInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(list.title || '');
  const { handlePopoverClick } = useHandlePopoverClick();

  // Sync title if list prop changes
  useEffect(() => {
    if (!isEditingTitle) setTitleValue(list.title || '');
  }, [list.title, isEditingTitle]);

  // Save title to API and update board state
  const saveTitle = useCallback(async (newTitle) => {
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed === list.title) {
      setTitleValue(list.title || '');
      setIsEditingTitle(false);
      return;
    }

    // Optimistic update
    setBoard((prev) => ({
      ...prev,
      actionLists: prev.actionLists.map((l) =>
        l._id === list._id ? { ...l, title: trimmed } : l
      ),
    }));
    setIsEditingTitle(false);

    const isTempList = typeof list._id === 'string' && list._id.startsWith('temp-');
    if (isTempList) return;

    try {
      await fetch(`${config.API_URI}/v1/actionLists/${list._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ boardId, title: trimmed }),
      });
    } catch {
      // Rollback on failure
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.map((l) =>
          l._id === list._id ? { ...l, title: list.title } : l
        ),
      }));
    }
  }, [list._id, list.title, boardId, setBoard]);

  const handleTitleClick = (e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.select(), 0);
  };

  const handleTitleBlur = () => {
    saveTitle(titleValue);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveTitle(titleValue);
    }
    if (e.key === 'Escape') {
      setTitleValue(list.title || '');
      setIsEditingTitle(false);
    }
  };

  // DnD: draggable header + drop target for cards and list reordering
  useEffect(() => {
    const element = headerRef.current;
    const handle = dragHandleRef.current;
    if (!element || isReadOnly) return;

    const cleanup = combine(
      draggable({
        element,
        dragHandle: handle || element,
        getInitialData: () => ({ ...list, type: 'ACTIONLIST' }),
        onDragStart() {
          setIsDragging(true);
          if (collapseAllGroups) collapseAllGroups();
        },
        onDrop() {
          setIsDragging(false);
          if (restoreCollapsedGroups) restoreCollapsedGroups();
        },
      }),
      dropTargetForElements({
        element,
        getIsSticky: () => true,
        getData: ({ input, element: el }) => {
          return attachClosestEdge({ ...list, type: 'LIST_DROP_TARGET' }, {
            input,
            element: el,
            allowedEdges: ['top', 'bottom'],
          });
        },
        canDrop: (args) =>
          args.source.data.type === 'CARD' || args.source.data.type === 'ACTIONLIST',
        onDragEnter(args) {
          if (args.source.data.type === 'CARD') {
            setIsDragOver(true);
            moveCard(args.source.data._id, list._id, 0);
          }
          if (args.source.data.type === 'ACTIONLIST' && args.source.data._id !== list._id) {
            const edge = extractClosestEdge(args.self.data);
            setClosestEdge(edge);
            if (moveActionList) moveActionList(args.source.data._id, list._id);
          }
        },
        onDrag(args) {
          if (args.source.data.type === 'ACTIONLIST' && args.source.data._id !== list._id) {
            const edge = extractClosestEdge(args.self.data);
            setClosestEdge(edge);
          }
        },
        onDragLeave() {
          setIsDragOver(false);
          setClosestEdge(null);
        },
        onDrop() {
          setIsDragOver(false);
          setClosestEdge(null);
        },
      })
    );

    return cleanup;
  }, [list, moveCard, moveActionList, isReadOnly, collapseAllGroups, restoreCollapsedGroups]);

  const listColor = list.color || 'var(--brand-color)';

  const getHeaderClass = () => {
    const classes = ['list-view-group-header'];
    if (isDragOver) classes.push('drag-over');
    if (isDragging) classes.push('dragging');
    if (closestEdge === 'top') classes.push('drop-indicator-top');
    if (closestEdge === 'bottom') classes.push('drop-indicator-bottom');
    return classes.join(' ');
  };

  const handleColorDotClick = (e) => {
    e.stopPropagation();
    if (isReadOnly) {
      onToggleCollapse();
      return;
    }
    handlePopoverClick(e, 'listColorPicker', {
      listId: list._id,
      boardId,
    });
  };

  const handleListAction = (e) => {
    e.stopPropagation();
    handlePopoverClick(e, 'listAction', {
      listId: list._id,
      boardId,
      workspaceId,
    });
  };

  return (
    <div className={getHeaderClass()} ref={headerRef}>
      {/* Frozen left section — stays visible during horizontal scroll */}
      <div className="row-frozen">
        {/* Drag handle */}
        {!isReadOnly && (
          <span className="group-drag-handle" ref={dragHandleRef}>
            <DragIndicator size={16} color="var(--muted-text-color)" />
          </span>
        )}

        <div className="group-header-left">
        <span className="collapse-icon" onClick={onToggleCollapse}>
          {isCollapsed
            ? <ChevronRight size={20} color="var(--secondary-text-color)" />
            : <ExpandMore size={20} color="var(--secondary-text-color)" />
          }
        </span>
        <span
          className="group-color-dot"
          id={`list-color-dot-${list._id}`}
          style={{ backgroundColor: listColor }}
          onClick={handleColorDotClick}
          data-popover-trigger
          data-tooltip-content="Change color"
          data-tooltip-position="top"
        />
        {isEditingTitle ? (
          <input
            ref={titleInputRef}
            className="group-title-input"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            onClick={(e) => e.stopPropagation()}
            spellCheck={false}
          />
        ) : (
          <span className="group-title" onClick={handleTitleClick}>{list.title}</span>
        )}
        <span className="group-count" onClick={onToggleCollapse}>{cardCount}</span>
        {!isReadOnly && (
          <>

            <span
              className="group-action-btn"
              id={`popover-action-list-lv-${list._id}`}
              onClick={handleListAction}
              data-popover-trigger
              data-tooltip-content="List actions"
              data-tooltip-position="top"
            >
              <MoreHoriz size={18} color="var(--secondary-text-color)" />
            </span>
            <span
              className="group-add-task-btn"
              onClick={(e) => { e.stopPropagation(); onAddCard?.(); }}
              data-tooltip-content="Add task"
              data-tooltip-position="top"
            >
              <Add size={18} color="var(--secondary-text-color)" />
            </span>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default ListViewGroupHeader;
